/**
 * Animated probability distribution bar showing bear / base / bull scenario
 * proportions. The segments grow in on mount and the expected-value marker
 * slides to its position, giving an instant visual read of where the
 * prediction sits within the scenario range.
 */

import React from "react";

interface ProbabilityBarProps {
  bearPct: number; // return %, e.g. -12.5
  basePct: number; // return %, e.g. 3.2
  bullPct: number; // return %, e.g. 18.0
  expectedPct: number; // return %, e.g. 2.1
}

export default function ProbabilityBar({
  bearPct,
  basePct,
  bullPct,
  expectedPct,
}: ProbabilityBarProps): React.ReactElement {
  // The full range spans bear to bull. Each segment's width is proportional
  // to the slice of the range it covers.
  const range = bullPct - bearPct;
  const safeRange = range > 0 ? range : 1;

  const bearWidth = ((basePct - bearPct) / safeRange) * 100;
  const baseWidth = ((expectedPct - basePct) / safeRange) * 100;
  const bullWidth = ((bullPct - expectedPct) / safeRange) * 100;

  // Position of the expected-value marker (0–100%).
  const markerPos = Math.max(0, Math.min(100, ((expectedPct - bearPct) / safeRange) * 100));

  return (
    <div className="space-y-1.5">
      <div className="relative flex h-7 w-full overflow-hidden rounded-lg bg-ink-800">
        {/* Bear segment */}
        <div
          className="prob-segment flex items-center justify-center bg-gradient-to-r from-signal-rose/40 to-signal-rose/20"
          style={{ flexGrow: 0, width: `${bearWidth}%` }}
        />
        {/* Base segment */}
        <div
          className="prob-segment flex items-center justify-center bg-gradient-to-r from-signal-amber/20 via-mist-400/20 to-signal-teal/20"
          style={{ flexGrow: 0, width: `${baseWidth}%` }}
        />
        {/* Bull segment */}
        <div
          className="prob-segment flex items-center justify-center bg-gradient-to-r from-signal-teal/20 to-signal-teal/40"
          style={{ flexGrow: 0, width: `${bullWidth}%` }}
        />

        {/* Expected-value marker */}
        <div
          className="absolute top-0 z-10 h-full w-0.5 bg-mist-100"
          style={{
            left: `${markerPos}%`,
            transition: "left 1s cubic-bezier(0.22, 1, 0.36, 1)",
            boxShadow: "0 0 8px rgba(237, 239, 245, 0.6)",
          }}
        >
          <div className="absolute -top-1 left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 rounded-sm bg-mist-100" />
        </div>
      </div>

      {/* Labels */}
      <div className="flex justify-between text-[10px] font-mono text-mist-500">
        <span className="text-signal-rose/80">
          {bearPct >= 0 ? "+" : ""}
          {bearPct.toFixed(1)}%
        </span>
        <span className="text-mist-300">
          {basePct >= 0 ? "+" : ""}
          {basePct.toFixed(1)}%
        </span>
        <span className="text-signal-teal/80">
          {bullPct >= 0 ? "+" : ""}
          {bullPct.toFixed(1)}%
        </span>
      </div>
    </div>
  );
}
